import { Controller } from "@hotwired/stimulus"

// Tab strip for application#show. Supports ?tab=… and in-page #section-… (document tab)
// by reading the URL on connect. Dispatches "application:document-tab-shown" for scroll-spy.
export default class extends Controller {
  static targets = ["btn", "panel"]
  static values = { default: { type: Number, default: 0 } }

  connect() {
    this._onHashChange = this._onHashChange.bind(this)
    window.addEventListener("hashchange", this._onHashChange)

    const index = this._resolveTabIndex()
    this.showTab(index, { scrollToHash: true, notifyDocument: true })
  }

  disconnect() {
    window.removeEventListener("hashchange", this._onHashChange)
  }

  _onHashChange() {
    if (/^#section-/.test(window.location.hash)) {
      const documentIndex = this._indexForKey("document")
      if (documentIndex >= 0) {
        this.showTab(documentIndex, { scrollToHash: true, notifyDocument: true })
      }
    }
  }

  /** Public for tests / external callers */
  selectByKey(key) {
    const i = this._indexForKey(key)
    if (i >= 0) this.showTab(i, { notifyDocument: true })
  }

  select(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    this.showTab(index, { notifyDocument: true })
    event.currentTarget.closest("details")?.removeAttribute("open")
  }

  _resolveTabIndex() {
    // Section anchor always lives in the "Documento" tab: hash wins over ?tab=…
    if (/^#section-\d+$/.test(window.location.hash)) {
      const di = this._indexForKey("document")
      if (di >= 0) return di
    }

    const fromQuery = this._indexForKey(
      new URLSearchParams(window.location.search).get("tab") || ""
    )
    if (fromQuery >= 0) return fromQuery

    return this.defaultValue
  }

  _indexForKey(key) {
    if (!key) return -1
    return this.btnTargets.findIndex((b) => b.dataset.key === key)
  }

  showTab(index, { scrollToHash = false, notifyDocument = false } = {}) {
    if (!this.btnTargets[index]) index = 0

    this.btnTargets.forEach((btn, i) => {
      if (i === index) {
        btn.classList.add("border-zinc-900", "text-zinc-900")
        btn.classList.remove("border-transparent", "text-zinc-500")
      } else {
        btn.classList.remove("border-zinc-900", "text-zinc-900")
        btn.classList.add("border-transparent", "text-zinc-500")
      }
    })

    this.panelTargets.forEach((panel, i) => {
      panel.classList.toggle("hidden", i !== index)
    })

    this._syncUrlTabParam(this.btnTargets[index]?.dataset.key)

    if (notifyDocument) {
      const key = this.btnTargets[index]?.dataset.key
      if (key === "document") {
        requestAnimationFrame(() => {
          window.dispatchEvent(new CustomEvent("application:document-tab-shown", { detail: { index } }))
        })
      }
    }

    if (scrollToHash) {
      requestAnimationFrame(() => {
        this._scrollToHash()
        requestAnimationFrame(() => {
          this._scrollToHash()
        })
      })
    }
  }

  _scrollToHash() {
    const raw = window.location.hash?.replace(/^#/, "")
    if (!raw || !raw.startsWith("section-")) return
    const el = document.getElementById(raw)
    if (el) el.scrollIntoView({ block: "start" })
  }

  _syncUrlTabParam(key) {
    if (!key) return
    const u = new URL(window.location.href)
    u.searchParams.set("tab", key)
    if (key !== "document" && /^#section-/.test(u.hash)) u.hash = ""

    const qs = u.searchParams.toString()
    const next = `${u.pathname}${qs ? `?${qs}` : ""}${u.hash || ""}`
    const now = `${window.location.pathname}${window.location.search || ""}${window.location.hash || ""}`
    if (next === now) return

    history.replaceState(null, "", next)
  }
}
